import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatINR } from "@/lib/sections";
import { toast, Toaster } from "sonner";
import { CheckCircle2, XCircle, RefreshCw, KeyRound, Eye, EyeOff } from "lucide-react";

const Stat = ({ label, value, testId }) => (
  <div className="border border-soft p-8 bg-white">
    <div className="overline">{label}</div>
    <div className="display text-4xl mt-3" data-testid={testId}>{value}</div>
  </div>
);

const HealthRow = ({ label, state, testId }) => {
  const Icon = state?.ok ? CheckCircle2 : XCircle;
  const color = state?.ok ? "#4A7A45" : "#B54A3F"; // sage / terracotta
  return (
    <div className="flex items-start gap-3 py-3 border-b border-soft last:border-0" data-testid={testId}>
      <Icon size={20} style={{ color }} className="mt-0.5 shrink-0" />
      <div className="flex-1">
        <div className="font-serif text-lg">{label}</div>
        <div className="text-xs text-muted2 mt-0.5 leading-relaxed">
          {state?.message || "Checking…"}
        </div>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    api.get("/admin/stats").then(r => setStats(r.data)).catch(() => {});
    runHealthCheck();
  }, []);

  const runHealthCheck = async () => {
    setChecking(true);
    try {
      const { data } = await api.get("/admin/health-check");
      setHealth(data);
    } catch (e) {
      setHealth({ error: true });
    } finally { setChecking(false); }
  };

  const checkedAt = health?.checked_at ? new Date(health.checked_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div data-testid="admin-dashboard">
      <div className="overline">Today at a glance</div>
      <h1 className="display text-5xl mt-3">Overview.</h1>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
        <Stat label="Revenue" value={stats ? formatINR(stats.revenue_inr) : "—"} testId="stat-revenue" />
        <Stat label="Paid bookings" value={stats ? stats.paid_bookings : "—"} testId="stat-paid" />
        <Stat label="Total bookings" value={stats ? stats.total_bookings : "—"} testId="stat-bookings" />
        <Stat label="Events live" value={stats ? `${stats.published_events}/${stats.total_events}` : "—"} testId="stat-events" />
        <Stat label="Contact messages" value={stats ? stats.contact_messages : "—"} testId="stat-contacts" />
      </div>

      {/* Integration health tile */}
      <div className="border border-soft bg-white mt-8 p-6" data-testid="health-tile">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="overline">Integrations</div>
            <div className="font-serif text-2xl mt-1">System status</div>
          </div>
          <button
            onClick={runHealthCheck}
            disabled={checking}
            className="btn-ghost text-xs"
            data-testid="health-refresh"
            title="Re-check now"
          >
            <RefreshCw size={12} className={checking ? "animate-spin" : ""} /> {checking ? "Checking…" : "Refresh"}
          </button>
        </div>
        <div className="mt-3">
          <HealthRow label="Razorpay" state={health?.razorpay} testId="health-razorpay" />
          <HealthRow label="Resend (email)" state={health?.resend} testId="health-resend" />
        </div>
        <div className="text-[10px] text-muted2 mt-3 tracking-wide uppercase">Last checked · {checkedAt}</div>
      </div>

      <p className="text-xs text-muted2 mt-8 max-w-xl leading-relaxed">
        Tip: Use the Events tab to create or edit programme listings. Bookings shows everyone who has paid (or attempted) — including payment IDs.
      </p>

      <DailyDigestPanel />
      <ChangePasswordPanel />
      <Toaster position="top-center" richColors />
    </div>
  );
}


function DailyDigestPanel() {
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!window.confirm("Send today's digest email to the admin address now?")) return;
    setSending(true);
    try {
      await api.post("/admin/digest/send-now");
      toast.success("Digest email sent — check your inbox.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Send failed"));
    } finally { setSending(false); }
  };
  return (
    <div className="border border-soft bg-white mt-8 p-6 max-w-xl" data-testid="digest-panel">
      <div className="overline">Automations</div>
      <div className="font-serif text-2xl mt-1">Daily digest</div>
      <p className="text-xs text-muted2 mt-2 mb-4 leading-relaxed">
        Every day at <strong>09:00 IST</strong> you'll receive an email summarising the last 24 hours — bookings (online + walk-ins), contact messages, gallery applications and revenue. Idempotent, so pod restarts won't cause duplicates.
      </p>
      <button onClick={send} disabled={sending} className="btn-ghost text-xs" data-testid="digest-send-now">
        {sending ? "Sending…" : "Send today's digest now"}
      </button>
    </div>
  );
}


function ChangePasswordPanel() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters.");
    if (next !== confirm) return toast.error("New passwords don't match.");
    if (next === current) return toast.error("New password must be different from the current one.");
    setSaving(true);
    try {
      await api.post("/admin/change-password", { current_password: current, new_password: next });
      toast.success("Password changed. Use the new one next time you sign in.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not change password"));
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-soft bg-white mt-8 p-6 max-w-xl" data-testid="change-password-panel">
      <div className="flex items-center gap-2">
        <KeyRound size={16} />
        <div className="overline">Security</div>
      </div>
      <div className="font-serif text-2xl mt-1">Change admin password</div>
      <p className="text-xs text-muted2 mt-2 mb-5 leading-relaxed">
        Please rotate the seeded password to something only you know. Minimum 8 characters.
      </p>
      <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
        <label className="block">
          <span className="overline">Current password</span>
          <input
            required
            type={show ? "text" : "password"}
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            className="w-full bg-transparent border-b border-soft py-2 text-sm focus:outline-none focus:border-charcoal"
            data-testid="cp-current"
          />
        </label>
        <label className="block">
          <span className="overline">New password (min 8)</span>
          <input
            required
            type={show ? "text" : "password"}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            className="w-full bg-transparent border-b border-soft py-2 text-sm focus:outline-none focus:border-charcoal"
            data-testid="cp-new"
          />
        </label>
        <label className="block">
          <span className="overline">Confirm new password</span>
          <input
            required
            type={show ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            className="w-full bg-transparent border-b border-soft py-2 text-sm focus:outline-none focus:border-charcoal"
            data-testid="cp-confirm"
          />
        </label>
        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            onClick={() => setShow(s => !s)}
            className="text-xs text-muted2 inline-flex items-center gap-1.5 hover:text-charcoal"
            data-testid="cp-toggle"
          >
            {show ? <EyeOff size={12} /> : <Eye size={12} />}
            {show ? "Hide" : "Show"} passwords
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary"
            data-testid="cp-submit"
          >
            {saving ? "Updating…" : "Change password"}
          </button>
        </div>
      </form>
    </div>
  );
}
